import { useEffect } from 'react';

/**
 * Tell the embedding page how tall this widget wants to be.
 *
 * An iframe cannot size itself: its height is set by the parent document, which
 * cannot see inside a cross-origin frame. So the widget measures itself and
 * posts the number out; the snippet's listener applies it.
 *
 * Two details that matter in someone else's page:
 *
 *   - ResizeObserver, not a one-shot measurement. The height changes when data
 *     arrives, when a calendar gains a day, and when the parent column is
 *     resized by the broker's own responsive layout.
 *
 *   - The message is posted to '*'. The widget does not know its parent's
 *     origin — that is precisely what makes document.referrer useful elsewhere —
 *     and there is nothing sensitive in "I am 312 pixels tall". The snippet's
 *     listener checks OUR origin before acting, which is the check that counts.
 */
export function useHeightReporter(): void {
  useEffect(() => {
    if (window.parent === window) return; // Not embedded; nothing to report to.

    let last = 0;

    const report = () => {
      // scrollHeight of the body, not innerHeight: we want the content's
      // natural height, not the height the parent currently gives us. Using the
      // latter would ratchet — each report would confirm the previous one and
      // the frame could never shrink.
      const height = Math.ceil(document.body.scrollHeight);
      if (height <= 0) return;
      // A 1px threshold stops sub-pixel rounding from producing an endless
      // resize/report loop between the frame and its parent.
      if (Math.abs(height - last) < 1) return;
      last = height;
      window.parent.postMessage({ type: 'mktwidget:height', height }, '*');
    };

    const observer = new ResizeObserver(report);
    observer.observe(document.body);

    // Fonts load after first paint and change measured height; without this the
    // first report is a few pixels short and the widget clips its last row.
    document.fonts?.ready.then(report).catch(() => undefined);

    report();

    return () => observer.disconnect();
  }, []);
}
